import { useState, useCallback, useEffect } from 'react'
import { leadsApi, mergeLeadStatuses } from '../../services/leadsApi'
import type { LeadStatusesResponse } from '../../services/leadsApi'

export { mergeLeadStatuses }
export type { LeadStatusesResponse }

/**
 * Custom hook to centralize lead status operations (fetching, adding, renaming, syncing)
 * and maintain consistent state across lead management components.
 *
 * @param smbId - Optional SMB (Small/Medium Business) ID.
 * @param initialStatuses - Optional initial list of lead statuses to populate state.
 */
export const useLeadStatuses = (
  smbId?: string,
  initialStatuses?: string[],
  onStatusesUpdated?: React.Dispatch<React.SetStateAction<string[]>> | ((statuses: string[]) => void)
) => {
  const [statuses, setStatuses] = useState<string[]>(initialStatuses || [])
  const [defaultStatuses, setDefaultStatuses] = useState<string[]>([])
  const [customStatuses, setCustomStatuses] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  /**
   * Fetches all lead statuses (default and custom) for the specified SMB.
   */
  const fetchStatuses = useCallback(async (): Promise<LeadStatusesResponse> => {
    if (!smbId) return { default: [], custom: [] }
    try {
      setLoading(true)
      const res = await leadsApi.getLeadStatuses(smbId)
      const defs = res.default || []
      const custs = res.custom || []
      setDefaultStatuses(defs)
      setCustomStatuses(custs)
      const merged = mergeLeadStatuses(res)
      setStatuses(merged)
      if (onStatusesUpdated) {
        onStatusesUpdated(merged)
      }
      return res
    } catch (err) {
      console.error('Failed to load lead statuses:', err)
      return { default: [], custom: [] }
    } finally {
      setLoading(false)
    }
  }, [smbId, onStatusesUpdated])

  /**
   * Adds a new custom lead status for the current SMB and updates local state.
   *
   * @param status - The name of the new custom status.
   */
  const addStatus = useCallback(
    async (status: string): Promise<LeadStatusesResponse> => {
      if (!smbId) throw new Error('SMB ID is required to add a lead status')
      try {
        setLoading(true)
        await leadsApi.addLeadStatus(smbId, status)
        return await fetchStatuses()
      } catch (err) {
        console.error('Failed to add lead status:', err)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [smbId, fetchStatuses]
  )

  /**
   * Renames an existing custom lead status for the current SMB and updates local state.
   *
   * @param oldStatus - The current status name to rename.
   * @param newStatus - The new status name to assign.
   */
  const renameStatus = useCallback(
    async (oldStatus: string, newStatus: string): Promise<LeadStatusesResponse> => {
      if (!smbId) throw new Error('SMB ID is required to rename a lead status')
      try {
        setLoading(true)
        await leadsApi.renameLeadStatus(smbId, oldStatus, newStatus)
        return await fetchStatuses()
      } catch (err) {
        console.error('Failed to rename lead status:', err)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [smbId, fetchStatuses]
  )

  /**
   * Synchronizes local state after status creation or editing and computes the next active status selection.
   */
  const syncStatusesAfterUpdate = useCallback(
    async (
      response?: LeadStatusesResponse,
      createdStatus?: string,
      currentSelectedStatus?: string
    ): Promise<{ mergedStatuses: string[]; resolvedStatus?: string }> => {
      let merged: string[]
      if (response) {
        merged = mergeLeadStatuses(response)
      } else if (smbId) {
        merged = await leadsApi.getAllLeadStatuses(smbId)
      } else {
        merged = statuses
      }

      setStatuses(merged)

      let resolvedStatus: string | undefined = currentSelectedStatus
      if (createdStatus) {
        resolvedStatus = createdStatus
      } else if (currentSelectedStatus) {
        const addedOrChanged = merged.find((s) => !statuses.includes(s))
        if (addedOrChanged) {
          resolvedStatus = addedOrChanged
        } else if (!merged.includes(currentSelectedStatus)) {
          resolvedStatus = 'New'
        }
      }

      return { mergedStatuses: merged, resolvedStatus }
    },
    [smbId, statuses]
  )

  /**
   * Helper function to handle status dialog completion, sync local & parent state, and resolve newly selected status.
   */
  const handleStatusComplete = useCallback(
    async (
      res?: LeadStatusesResponse,
      createdStatus?: string,
      currentSelectedStatus?: string,
      onStatusSelected?: (status: string) => void,
      updateParentCustomStatuses?: (statuses: string[]) => void
    ) => {
      const { mergedStatuses, resolvedStatus } = await syncStatusesAfterUpdate(
        res,
        createdStatus,
        currentSelectedStatus
      )
      if (updateParentCustomStatuses) {
        updateParentCustomStatuses(mergedStatuses)
      }
      if (resolvedStatus && onStatusSelected) {
        onStatusSelected(resolvedStatus)
      }
      return { mergedStatuses, resolvedStatus }
    },
    [syncStatusesAfterUpdate]
  )

  useEffect(() => {
    if (initialStatuses !== undefined) {
      setStatuses(initialStatuses)
    }
  }, [initialStatuses])

  return {
    statuses,
    defaultStatuses,
    customStatuses,
    setStatuses,
    fetchStatuses,
    addStatus,
    renameStatus,
    syncStatusesAfterUpdate,
    handleStatusComplete,
    loading,
  }
}
